import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import ProjectItemList, { ProjectItem } from '../components/project/ProjectItemList';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import BreadcrumbBack from '../components/BreadcrumbBack';
import CreateMediaModal from '../components/CreateMediaModal';
import FullPagePaymentWall, { PaymentWallConfirmation, PaymentWallPreflight } from '../components/FullPagePaymentWall';
import { uploadToR2 } from '../utils/r2Upload';
import { runPayNowFlow } from '../utils/payNowFlow';
import { ShortsMode, ReframeMode } from '../utils/shorts';
import { KineticCaptionSettings } from '../components/KineticCaptionPicker';
import EditProjectModal, { EditProjectTab } from '../components/EditProjectModal';
import { can, isOwnerAccess, ProjectAccess } from '../hooks/useProjectAccess';
import { track } from '../utils/analytics';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface Project {
  id: string;
  name: string;
  description: string | null;
  platform: string;
  audience: string;
  default_linked_account_id?: string | null;
  access?: ProjectAccess;
  video_count: number;
  shorts_count: number;
  updated_at: string;
}

interface VideoItem {
  id: string;
  name: string | null;
  description: string | null;
  publish_status: string;
  file_name: string;
  duration: number | null;
  status: string;
  shorts_count: number;
  avg_score: number;
  created_at: string;
  platform: string;
  content_type: string;
  caption_workspaces: { id: string; name: string; status: string }[];
}

interface StoryItem {
  id: string;
  name: string;
  status: string;
  videoId: string | null;
  slideCount: number;
  updatedAt: string;
  thumbnailUrl: string | null;
}

/** Options for regenerating shorts from an already-uploaded video (the /copy route). */
interface ExistingVideoOptions {
  name?: string;
  description?: string;
  kineticCaption?: KineticCaptionSettings;
  mode?: ShortsMode;
  reframeMode?: ReframeMode;
  createSummary?: boolean;
}

interface PendingVideoConfirmAction {
  videoId: string;
  whisperModel: string;
  createSummary?: boolean;
  mode?: ShortsMode;
  reframeMode?: ReframeMode;
  /** Set when the blocked action is a regenerate-from-existing, not an upload confirm. */
  copyOptions?: ExistingVideoOptions;
}

interface UploadPaymentWallState {
  preflight: PaymentWallPreflight;
  action: PendingVideoConfirmAction;
}

function isPaymentRequiredPayload(payload: unknown): payload is PaymentWallPreflight {
  if (!payload || typeof payload !== 'object') return false;
  const obj = payload as Record<string, unknown>;
  return obj.status === 'payment_required'
    && typeof obj.featureKey === 'string'
    && typeof obj.units === 'number'
    && typeof obj.includedUnitsRemaining === 'number'
    && typeof obj.paygOption === 'object'
    && obj.paygOption !== null
    && typeof (obj.paygOption as Record<string, unknown>).unitPrice === 'number'
    && typeof (obj.paygOption as Record<string, unknown>).estimatedAmount === 'number'
    && typeof obj.plansOption === 'object'
    && obj.plansOption !== null
    && Array.isArray((obj.plansOption as Record<string, unknown>).recommendedPlanIds);
}

const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [stories, setStories] = useState<StoryItem[]>([]);
  // Stories, captions and uploads in one shape — what the list actually renders.
  const [items, setItems] = useState<ProjectItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentWall, setPaymentWall] = useState<UploadPaymentWallState | null>(null);
  const [processingPaymentWall, setProcessingPaymentWall] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editTab, setEditTab] = useState<EditProjectTab>('general');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const pendingChargeAuthorizationIdRef = useRef<string | null>(null);
  // Shared (read-only) projects: hide what the caller's role can't do. The API enforces it too.
  const isOwner = isOwnerAccess(project?.access);
  const canCaption = can(project?.access, 'can_edit_caption');
  const canStory = can(project?.access, 'can_edit_story');
  const canClip = can(project?.access, 'can_edit_clip');
  const canPublish = can(project?.access, 'can_publish');

  const fetchData = useCallback(async () => {
    try {
      const [pRes, vRes, sRes, iRes] = await Promise.all([
        fetch(`${API_BASE}/api/projects/${id}`, { credentials: 'include' }),
        fetch(`${API_BASE}/api/projects/${id}/videos`, { credentials: 'include' }),
        fetch(`${API_BASE}/api/projects/${id}/image-video`, { credentials: 'include' }),
        fetch(`${API_BASE}/api/projects/${id}/items`, { credentials: 'include' }),
      ]);
      if (pRes.ok) setProject(await pRes.json());
      else navigate('/projects');
      if (vRes.ok) setVideos(await vRes.json());
      if (sRes.ok) setStories(await sRes.json());
      if (iRes.ok) setItems(await iRes.json());
    } catch { /* ignore */ }
    setLoading(false);
  }, [id, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Poll while anything in the list is mid-render — a story, a caption or an upload alike.
  useEffect(() => {
    const settled = ['completed', 'done', 'failed', 'pending', 'ready', 'draft'];
    const busy = (list: { status: string }[]) => list.some(i => !settled.includes((i.status || '').toLowerCase()));
    if (!busy(items) && !busy(videos)) return;

    pollRef.current = setInterval(async () => {
      const [vRes, iRes] = await Promise.all([
        fetch(`${API_BASE}/api/projects/${id}/videos`, { credentials: 'include' }),
        fetch(`${API_BASE}/api/projects/${id}/items`, { credentials: 'include' }),
      ]);
      const nextVideos = vRes.ok ? await vRes.json() : videos;
      const nextItems = iRes.ok ? await iRes.json() : items;
      setVideos(nextVideos);
      setItems(nextItems);
      if (!busy(nextItems) && !busy(nextVideos)) {
        if (pollRef.current) clearInterval(pollRef.current);
      }
    }, 2000);

    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [items, videos, id]);

  const confirmVideoProcessing = useCallback(async (
    videoId: string,
    whisperModel: string,
    chargeAuthorizationId?: string,
    createSummary?: boolean,
    mode?: ShortsMode,
    reframeMode?: ReframeMode,
  ): Promise<'queued' | 'payment_required'> => {
    const confirmRes = await fetch(`${API_BASE}/api/projects/${id}/videos/confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        videoId,
        whisperModel,
        ...(chargeAuthorizationId ? { chargeAuthorizationId } : {}),
        ...(createSummary ? { createSummary: true } : {}),
        ...(mode === 'studio' ? { mode } : {}),
        ...(reframeMode === 'fit-all-faces' ? { reframeMode } : {}),
      }),
    });

    if (confirmRes.status === 402) {
      const payload = await confirmRes.json().catch(() => null);
      if (isPaymentRequiredPayload(payload)) {
        setPaymentWall({
          preflight: payload,
          action: { videoId, whisperModel, createSummary, mode, reframeMode },
        });
        return 'payment_required';
      }
      throw new Error('Usage limit reached. Open billing to continue.');
    }

    if (!confirmRes.ok) {
      const data = await confirmRes.json().catch(() => ({ error: 'Confirm failed' }));
      throw new Error(data.error || 'Confirm failed');
    }

    track('shorts_job_queued', { mode: mode === 'studio' ? 'studio' : 'classic' });
    return 'queued';
  }, [id]);

  const handleUploadVideo = async (file: File, _name: string, _description: string, _language: string, _whisperModel: string, _createSummary?: boolean, _mode?: ShortsMode, _reframeMode?: ReframeMode) => {
    if (!id) {
      setError('Project not found');
      return;
    }

    setCreateModalOpen(false);
    setUploading(true);
    setError(null);
    setPaymentWall(null);
    pendingChargeAuthorizationIdRef.current = null;
    track('video_upload_started', { size_mb: Math.round(file.size / 1048576) });

    try {
      // 1. Init — get presigned URL
      const initRes = await fetch(`${API_BASE}/api/projects/${id}/videos/init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          fileName: file.name,
          fileSize: file.size,
          name: _name,
          description: _description,
          platform: project?.platform || 'youtube_shorts',
          audience: project?.audience || 'general',
          language: _language,
          whisperModel: _whisperModel,
        }),
      });
      if (!initRes.ok) {
        const data = await initRes.json().catch(() => ({ error: 'Init failed' }));
        throw new Error(data.error);
      }
      const { videoId, presignedUrl } = await initRes.json();

      // 2. Upload directly to R2
      await uploadToR2(presignedUrl, file);

      // 3. Confirm — trigger processing
      const confirmStatus = await confirmVideoProcessing(videoId, _whisperModel, undefined, _createSummary, _mode, _reframeMode);
      if (confirmStatus === 'payment_required') {
        return;
      }

      await fetchData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const copyExistingVideo = async (
    videoId: string,
    options?: ExistingVideoOptions,
    chargeAuthorizationId?: string,
  ): Promise<'queued' | 'payment_required'> => {
    const res = await fetch(`${API_BASE}/api/projects/${id}/videos/${videoId}/copy`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      // kineticCaption is omitted entirely when captions are off — never an empty object.
      body: JSON.stringify({
        ...(options?.name ? { name: options.name } : {}),
        ...(options?.description ? { description: options.description } : {}),
        ...(options?.kineticCaption ? { kineticCaption: options.kineticCaption } : {}),
        ...(options?.mode ? { mode: options.mode } : {}),
        ...(options?.reframeMode ? { reframeMode: options.reframeMode } : {}),
        createSummary: options?.createSummary === true,
        ...(chargeAuthorizationId ? { chargeAuthorizationId } : {}),
      }),
    });

    if (res.status === 402) {
      const payload = await res.json().catch(() => null);
      if (isPaymentRequiredPayload(payload)) {
        setPaymentWall({
          preflight: payload,
          action: { videoId, whisperModel: '', copyOptions: options },
        });
        return 'payment_required';
      }
      throw new Error('Usage limit reached. Open billing to continue.');
    }

    if (!res.ok) {
      const data = await res.json().catch(() => ({ error: 'Failed to start shorts generation' }));
      throw new Error(data.error || 'Failed to start shorts generation');
    }
    return 'queued';
  };

  const handleUseExistingVideo = async (videoId: string, options?: ExistingVideoOptions) => {
    if (!id) {
      setError('Project not found');
      return;
    }

    setCreateModalOpen(false);
    setUploading(true);
    setError(null);
    setPaymentWall(null);
    pendingChargeAuthorizationIdRef.current = null;

    try {
      const status = await copyExistingVideo(videoId, options);
      if (status === 'payment_required') return;
      await fetchData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const closePaymentWall = () => {
    if (processingPaymentWall) return;
    pendingChargeAuthorizationIdRef.current = null;
    setPaymentWall(null);
  };

  const handleShowPlans = () => {
    setError(null);
  };

  const handlePayNowAuthorization = async (): Promise<PaymentWallConfirmation> => {
    if (!paymentWall || !id) {
      throw new Error('Payment wall context is unavailable.');
    }
    const wall = paymentWall;

    setProcessingPaymentWall(true);
    setError(null);

    try {
      const methodsRes = await fetch(`${API_BASE}/api/me/billing/payment-methods`, {
        credentials: 'include',
      });

      if (methodsRes.ok) {
        const methodsData = await methodsRes.json().catch(() => null) as { paymentMethods?: unknown[] } | null;
        const paymentMethods: unknown[] = Array.isArray(methodsData?.paymentMethods)
          ? (methodsData?.paymentMethods as unknown[])
          : [];
        if (paymentMethods.length === 0) {
          window.location.assign('/account/billing');
          throw new Error('No payment method found. Add a payment method to continue.');
        }
      }

      const payment = await runPayNowFlow({
        featureKey: wall.preflight.featureKey,
        units: wall.preflight.units,
        context: {
          projectId: id,
          videoId: wall.action.videoId,
        },
        pricingSnapshot: {
          unitPrice: wall.preflight.paygOption.unitPrice,
          currency: wall.preflight.paygOption.currency,
        },
        fallbackAmount: wall.preflight.paygOption.estimatedAmount,
        fallbackCurrency: wall.preflight.paygOption.currency,
      });

      pendingChargeAuthorizationIdRef.current = payment.chargeAuthorizationId;

      return {
        outcome: 'payment_authorized',
        amount: payment.amount,
        currency: payment.currency,
      };
    } catch (err: any) {
      if (typeof err?.message === 'string' && /payment method/i.test(err.message)) {
        window.location.assign('/account/billing');
      }
      setError(err.message);
      throw err;
    } finally {
      setProcessingPaymentWall(false);
    }
  };

  const handleSwitchPlanOnly = async (planId: string): Promise<PaymentWallConfirmation> => {
    if (!paymentWall || !id) {
      throw new Error('Payment wall context is unavailable.');
    }

    setProcessingPaymentWall(true);
    setError(null);

    try {
      const modeRes = await fetch(`${API_BASE}/api/me/pricing/mode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          mode: 'subscription',
          planId,
        }),
      });

      if (!modeRes.ok) {
        const data = await modeRes.json().catch(() => ({ error: 'Failed to switch plan' }));
        throw new Error(data.error || 'Failed to switch plan');
      }

      const modeData = await modeRes.json().catch(() => null) as { assignedPlan?: { name?: string; priceMonthly?: number } } | null;

      return {
        outcome: 'plan_updated',
        planName: modeData?.assignedPlan?.name,
        planPriceMonthly: modeData?.assignedPlan?.priceMonthly,
      };
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setProcessingPaymentWall(false);
    }
  };

  const handleResumeBlockedAction = async (): Promise<void> => {
    if (!paymentWall || !id) return;
    const wall = paymentWall;

    setProcessingPaymentWall(true);
    setError(null);

    try {
      const chargeAuthorizationId = pendingChargeAuthorizationIdRef.current || undefined;
      const confirmStatus = wall.action.copyOptions
        ? await copyExistingVideo(wall.action.videoId, wall.action.copyOptions, chargeAuthorizationId)
        : await confirmVideoProcessing(
          wall.action.videoId,
          wall.action.whisperModel,
          chargeAuthorizationId,
          wall.action.createSummary,
          wall.action.mode,
          wall.action.reframeMode,
        );
      if (confirmStatus === 'payment_required') {
        throw new Error('Payment confirmation is still pending. Please retry in a moment.');
      }

      pendingChargeAuthorizationIdRef.current = null;
      setPaymentWall(null);
      await fetchData();
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setProcessingPaymentWall(false);
    }
  };

  const handleFromImages = async (_name: string, _description: string) => {
    setCreateModalOpen(false);
    // Create the Story up front so the editor opens already named and reopenable.
    if (_name.trim()) {
      try {
        const res = await fetch(`${API_BASE}/api/projects/${id}/image-video`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ name: _name.trim() }),
        });
        if (res.ok) {
          const story = await res.json();
          navigate(`/projects/${id}/image-video/${story.id}`);
          return;
        }
      } catch { /* fall through to the unnamed editor */ }
    }
    const params = new URLSearchParams();
    if (_name) params.set('name', _name);
    if (_description) params.set('description', _description);
    const qs = params.toString();
    navigate(`/projects/${id}/image-video${qs ? `?${qs}` : ''}`);
  };

  const handleFromLink = async (
    _linkUrl: string,
    _name: string,
    _description: string,
    _language: string,
    _whisperModel: string,
    _mode?: ShortsMode,
    _reframeMode?: ReframeMode,
  ) => {
    setCreateModalOpen(false);
    setUploading(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/api/projects/${id}/videos/link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          linkUrl: _linkUrl,
          name: _name,
          description: _description,
          language: _language,
          whisperModel: _whisperModel,
          platform: project?.platform || 'youtube_shorts',
          audience: project?.audience || 'general',
          ...(_mode === 'studio' ? { mode: _mode } : {}),
          ...(_reframeMode === 'fit-all-faces' ? { reframeMode: _reframeMode } : {}),
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: 'Failed to create link media job' }));
        throw new Error(data.error || 'Failed to create link media job');
      }

      await fetchData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  // Soft archive: the item stays, the list moves it under the Archive chip.
  const handleArchiveItem = async (item: ProjectItem) => {
    try {
      const res = await fetch(`${API_BASE}/api/projects/${id}/items/${item.kind}/${item.id}/archive`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) throw new Error('Could not archive');
      setItems(prev => prev.map(i => (i.kind === item.kind && i.id === item.id ? { ...i, archived: true } : i)));
    } catch {
      setError('Could not archive this item. Try again.');
    }
  };

  const handleDeleteProject = async () => {
    if (!window.confirm('Archive this project?')) return;
    await fetch(`${API_BASE}/api/projects/${id}`, { method: 'DELETE', credentials: 'include' });
    navigate('/projects');
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target as Node)) {
        setMoreMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '—';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const formatRelativeDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const statusClass = (status: string) => {
    if (status === 'completed') return 'video-status-done';
    if (status === 'failed') return 'video-status-failed';
    if (status === 'pending') return 'video-status-pending';
    return 'video-status-processing';
  };

  const statusLabel = (status: string) => {
    if (status === 'completed') return '✅ Completed';
    if (status === 'failed') return '❌ Failed';
    if (status === 'pending') return '⏳ Pending';
    return `⚙️ ${status.charAt(0).toUpperCase() + status.slice(1)}...`;
  };

  if (loading) return <div className="dashboard-layout"><Sidebar /><main className="main-content"><Header /><div className="project-loading">Loading...</div></main></div>;

  if (paymentWall) {
    return (
      <div className="dashboard-layout">
        <Sidebar />
        <main className="main-content">
          <Header />

          <div className="breadcrumb">
            <BreadcrumbBack to="/projects" />
            <Link to="/projects">Projects</Link>
            <span className="breadcrumb-sep">›</span>
            <span>{project?.name}</span>
          </div>

          {error && <div className="error-banner">⚠️ {error}</div>}

          <FullPagePaymentWall
            preflight={paymentWall.preflight}
            busy={processingPaymentWall}
            onPayNow={handlePayNowAuthorization}
            onShowPlans={handleShowPlans}
            onSwitchPlan={handleSwitchPlanOnly}
            onResumeBlockedAction={handleResumeBlockedAction}
            onCancel={closePaymentWall}
          />
        </main>
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="main-content">
        <Header />

        <div className="breadcrumb">
          <BreadcrumbBack to="/projects" />
          <Link to="/projects">Projects</Link>
          <span className="breadcrumb-sep">›</span>
          <span>{project?.name}</span>
        </div>

        <div className="project-detail-header">
          <div>
            <h2>{project?.name}</h2>
            <p className="project-detail-meta">
              {project?.video_count} video{project?.video_count !== 1 ? 's' : ''} · {project?.shorts_count} short{project?.shorts_count !== 1 ? 's' : ''}
              {project?.description && <> · {project.description}</>}
            </p>
          </div>
          <div className="project-detail-actions">
            {canCaption && (
              <Link to={`/projects/${id}/kinetic-caption`} className="header-action-btn">
                Caption
              </Link>
            )}
            {canStory && (
              <Link to={`/projects/${id}/image-video`} className="header-action-btn">
                Story
              </Link>
            )}
            {isOwner && (
              <button
                className="header-action-btn header-action-primary"
                onClick={() => setCreateModalOpen(true)}
                disabled={uploading}
              >
                {uploading ? '⏳ Uploading...' : '⚡ Quick Shorts'}
              </button>
            )}
            {!isOwner && <span className="project-shared-badge" title={`Shared with you as ${project?.access?.role}`}>👁 Read-only</span>}
            {isOwner && (
            <div className="project-action-dropdown" ref={moreMenuRef}>
              <button
                className="header-action-btn"
                onClick={() => setMoreMenuOpen(!moreMenuOpen)}
              >
                ⋯
              </button>
              {moreMenuOpen && (
                <div className="project-card-dropdown project-more-menu">
                  <button onClick={() => { setEditTab('general'); setEditOpen(true); setMoreMenuOpen(false); }} title="Set the default publishing account">
                    👤 Set Default Publishing Account
                  </button>
                  <button onClick={() => { setEditTab('general'); setEditOpen(true); setMoreMenuOpen(false); }}>
                    ✏️ Edit Project
                  </button>
                  <button onClick={() => { navigate(`/projects/${id}/y-ray`); setMoreMenuOpen(false); }} title="See who is on screen and what is said, moment by moment">
                    🔎 Y-Ray
                  </button>
                  <button onClick={() => { setEditTab('share'); setEditOpen(true); setMoreMenuOpen(false); }}>
                    🔗 Share as Demo
                  </button>
                  <button className="danger" onClick={() => { handleDeleteProject(); setMoreMenuOpen(false); }}>
                    🗑 Delete
                  </button>
                </div>
              )}
            </div>
            )}
          </div>
        </div>

        {editOpen && project && (
          <EditProjectModal
            project={project}
            initialTab={editTab}
            onClose={() => setEditOpen(false)}
            onSaved={(updated) => { setProject(prev => (prev ? { ...prev, ...updated } : prev)); setEditOpen(false); }}
          />
        )}

        <CreateMediaModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onUploadVideo={handleUploadVideo}
          onFromImages={handleFromImages}
          onFromLink={handleFromLink}
          onUseExistingVideo={handleUseExistingVideo}
          projectId={id}
          uploading={uploading}
        />

        {error && <div className="error-banner">⚠️ {error}</div>}

        {items.length > 0 && (
          <ProjectItemList
            items={items}
            // Opening an item lands on its read-only viewer — its renders and a player. The
            // editors keep their own routes and are one click away from there.
            onOpen={(item) => navigate(`/projects/${id}/item/${item.kind}/${item.id}`)}
            onPlay={(item) => { if (item.latestRenderUrl) window.open(item.latestRenderUrl, '_blank'); }}
            onOpenSourceCaption={canCaption ? (workspaceId) => navigate(`/projects/${id}/kinetic-caption/${workspaceId}`) : undefined}
            // Owner only — on a shared project the Archive chip and card button are hidden.
            onArchive={isOwner ? handleArchiveItem : undefined}
            shortsActions={canClip || canPublish ? (item) => (
              <>
                {canClip && <Link to={`/projects/${id}/video/${item.id}/clip/first`} className="btn-sm">🎬 Clips</Link>}
                {canPublish && <Link to={`/projects/${id}/video/${item.id}/publish`} className="btn-sm btn-sm-accent">📤 Publish</Link>}
              </>
            ) : undefined}
          />
        )}

        {items.length === 0 ? (
          <div className="project-empty">
            <div className="empty-icon">🎬</div>
            <h3>No videos yet</h3>
            <p>{isOwner ? 'Upload your first video to start generating shorts.' : 'Nothing has been added to this project yet.'}</p>
            {isOwner && (
              <div className="project-detail-actions">
                <Link to={`/projects/${id}/kinetic-caption`} className="btn-secondary">Caption</Link>
                <Link to={`/projects/${id}/image-video`} className="btn-secondary">Story</Link>
                <button className="btn-primary" onClick={() => setCreateModalOpen(true)}>⚡ Quick Shorts</button>
              </div>
            )}
          </div>
        ) : null}
      </main>
    </div>
  );
};

export default ProjectDetail;
